import React, { useState, useMemo } from 'react'
import { Modal } from '~/components/Modal/Modal'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { FormattedMessage } from 'react-intl'
import type { BaseConfig } from '~/utils/configTypes'
import type { ProductKey } from '~/utils/configUrl'
import { buildConfigShareUrl } from '~/utils/configShareUrl'
import styles from './ShareConfigModal.module.css'
import ShareIcon from '@mui/icons-material/Share'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'

interface ShareConfigModalBaseProps {
  isOpen: boolean
  onClose: () => void
}

interface ShareConfigModalWithConfig extends ShareConfigModalBaseProps {
  config: BaseConfig
  product: ProductKey
  locale: string
  url?: never
}

interface ShareConfigModalWithUrl extends ShareConfigModalBaseProps {
  url: string
  config?: never
  product?: never
  locale?: never
}

type ShareConfigModalProps =
  | ShareConfigModalWithConfig
  | ShareConfigModalWithUrl

export const ShareConfigModal: React.FC<ShareConfigModalProps> = (props) => {
  const { isOpen, onClose } = props
  const [copied, setCopied] = useState(false)

  const externalUrl = 'url' in props ? props.url : undefined
  const config = 'config' in props ? props.config : undefined
  const product = 'product' in props ? props.product : undefined
  const locale = 'locale' in props ? props.locale : undefined

  const shareableUrl = useMemo(() => {
    if (externalUrl) return externalUrl
    if (typeof window === 'undefined' || !config || !product || !locale)
      return ''

    return buildConfigShareUrl(config, product, locale === 'ru' ? 'ru' : 'ro')
  }, [externalUrl, config, product, locale])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.iconContainer}>
          <ShareIcon className={styles.icon} />
        </div>
        <h3 className={styles.title}>
          <FormattedMessage id="modal.shareConfig.title" />
        </h3>
        <p className={styles.description}>
          <FormattedMessage id="modal.shareConfig.description" />
        </p>

        <div className={styles.urlContainer}>
          <input
            type="text"
            value={shareableUrl}
            readOnly
            className={styles.urlInput}
            onClick={(e) => e.currentTarget.select()}
          />
        </div>

        <div className={styles.buttonContainer}>
          <CustomButton
            icon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
            variant="primary"
            size="medium"
          >
            {copied ? (
              <FormattedMessage id="modal.shareConfig.copied" />
            ) : (
              <FormattedMessage id="modal.shareConfig.copyLink" />
            )}
          </CustomButton>
        </div>
      </div>
    </Modal>
  )
}
